import { createEmptyProduct as createBaseProduct } from 'composables/useOcrProducts';

const createProduct = (options = {}) => {
    return { ...createBaseProduct(), ...options };
};

const isDefined = (data, key) => {
    return data[key] !== undefined && data[key].value !== 'N/A';
};

const findTaxIdByRate = (taxList, rate) => {
    return taxList.find((tax) => parseFloat(tax.rate) === parseFloat(rate))?.id ?? null;
};

const getDefaultAccountCode = (accountingCodes, defaultAccountingCode) => {
    return accountingCodes.find((code) => code.id === defaultAccountingCode);
};

const formatDateString = (dateValue) => {
    if (!dateValue || dateValue.length === 0) {
        return '';
    }
    return dateValue.split(/[T ]/)[0];
};

const fieldValue = (field, fallback = null) => {
    if (field === null || field === undefined) {
        return fallback;
    }
    const val = field.value ?? field;
    return val === null || val === undefined ? fallback : val;
};

const formatAmount = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
        return null;
    }
    return parsed.toFixed(2).replace('.', ',');
};

const hydrateCatalogueLink = (item) => {
    const link = item.catalogue_link;
    if (!link?.id) {
        return null;
    }
    return { id: link.id, label: link.label, value: link.id };
};

const hydrateLineItem = (item, params, baseOptions) => {
    const quantity = parseFloat(fieldValue(item.quantity, 1)) || 1;
    const unitPrice = fieldValue(item.unit_price);
    const taxRate = fieldValue(item.tax_rate);
    const taxId = taxRate !== null ? findTaxIdByRate(params.taxList, taxRate) : null;
    const analyticCode = item.analytics_code?.value ?? '';
    const catalogueLink = hydrateCatalogueLink(item);
    const description =
        fieldValue(item.description) ||
        fieldValue(item.product_code) ||
        catalogueLink?.label ||
        baseOptions.description ||
        '';
    const accountingCode = item.accounting_code ?? baseOptions.accountingCode ?? null;

    return createProduct({
        ...baseOptions,
        description,
        quantity,
        priceWithoutTax: formatAmount(unitPrice),
        taxRateId: taxId,
        analyticCode,
        catalogueLink,
        accountingCode,
    });
};

export const hydrateOcr = (data, params) => {
    const accountCode = getDefaultAccountCode(params.accountingCodes, params.defaultAccountingCode);

    const baseProductOptions = {
        description: accountCode?.label ?? '',
        accountingCode: accountCode ? params.defaultAccountingCode : null,
    };

    const hasSupplier = data.supplier?.id !== null && data.supplier?.id !== undefined;
    const invoiceDate = isDefined(data, 'invoice_date') ? data.invoice_date.value : '';
    const dueDate = isDefined(data, 'due_date') ? data.due_date.value : '';

    const lineItems = data.line_items;
    const hasLineItems = Array.isArray(lineItems) && lineItems.length > 0;

    let analyticsMode = null;
    if (data.analytics_code !== undefined) {
        analyticsMode = 'doc';
    } else if (hasLineItems && lineItems[0].analytics_code !== undefined) {
        analyticsMode = 'item';
    }

    const ocr = {
        supplier: hasSupplier ? +data.supplier.id : null,
        documentDate: formatDateString(invoiceDate),
        dueDate: formatDateString(dueDate || invoiceDate),
        currency: isDefined(data, 'locale') ? data.locale.value : '',
        totalWithoutTax: isDefined(data, 'total_excl') ? data.total_excl.value : null,
        totalWithTax: isDefined(data, 'total_incl') ? data.total_incl.value : null,
        externalDocumentName: isDefined(data, 'invoice_number') ? data.invoice_number.value : null,
    };

    if (analyticsMode === 'doc') {
        ocr.analyticCode = data.analytics_code?.value ?? '';
    }

    if (hasLineItems) {
        ocr.products = lineItems.map((item) => hydrateLineItem(item, params, baseProductOptions));
    } else if (isDefined(data, 'taxes')) {
        const taxes = data.taxes;

        if (taxes.length <= 1) {
            const taxId =
                taxes.length === 1 ? findTaxIdByRate(params.taxList, taxes[0].rate) : null;
            ocr.products = [
                createProduct({
                    ...baseProductOptions,
                    taxRateId: taxId,
                    priceWithoutTax: formatAmount(ocr.totalWithoutTax),
                }),
            ];
        } else {
            ocr.products = taxes.map((tax) =>
                createProduct({
                    ...baseProductOptions,
                    taxRateId: findTaxIdByRate(params.taxList, tax.rate),
                }),
            );
        }
    }

    return { formData: ocr, analyticsMode };
};

export { createProduct };
